/**
@license
Copyright (c) 2019 Behavio Labs s.r.o. All rights reserved.
*/

import { css, customElement, html, property } from 'lit-element';
import { installMediaQueryWatcher } from 'pwa-helpers/media-query';
import { installOfflineWatcher } from 'pwa-helpers/network';
import '@material/mwc-drawer/mwc-drawer';
import '@material/mwc-top-app-bar/mwc-top-app-bar';
import '@material/mwc-icon-button/mwc-icon-button';
import { registerStyles } from '@vaadin/vaadin-themable-mixin/register-styles';

import { CoreElement } from './core-element';
import { logChangedProperties } from '../modules/helpers';
import './snack-bar';

const style = css`
  :host {
    display: block;

    --app-drawer-width: 256px;
    --app-primary-color: #6200ee;
    --app-secondary-color: #293237;
    --app-dark-text-color: var(--app-secondary-color);
    --app-light-text-color: white;
    --app-section-even-color: #f7f7f7;
    --app-section-odd-color: white;
    --app-header-background-color: #60d0e4;
    --app-header-text-color: white;
    --app-header-selected-color: var(--app-primary-color);
    --app-drawer-background-color: white;
    --app-drawer-text-color: var(--app-secondary-color);
    --app-drawer-selected-color: #78909c;
    --app-border-radius: 4px;
    --app-gutter: 24px;
    --mdc-theme-secondary: var(--app-primary-color);
  }

  [hidden] {
    display: none !important;
  }

  app-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    color: var(--app-header-text-color);
    text-align: center;
    background-color: var(--app-header-background-color);
    border-bottom: 1px solid #eee;
  }

  b {
    font-weight: 500;
  }

  .toolbar-top {
    background-color: var(--app-header-background-color);
  }

  [main-title] {
    /* In the narrow layout, the toolbar is offset by the width of the
    drawer button, and the text looks not centered. Add a padding to
    match that button */
    padding-right: 44px;
    font-size: 30px;
  }

  .toolbar-list {
    display: none;
  }

  .toolbar-list > a {
    display: inline-block;
    padding: 4px 24px;
    line-height: 30px;
    color: var(--app-header-text-color);
    text-decoration: none;
  }

  .toolbar-list > a[selected] {
    color: var(--app-header-selected-color);
    border-bottom: 4px solid var(--app-header-selected-color);
  }

  .menu-btn {
    width: 44px;
    height: 44px;
    cursor: pointer;
    background: none;
    border: none;
    fill: var(--app-header-text-color);
  }

  .drawer-list {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 24px;
    background: var(--app-drawer-background-color);
  }

  .drawer-list > a {
    display: block;
    padding: 0 24px;
    line-height: 40px;
    color: var(--app-drawer-text-color);
    text-decoration: none;
  }

  .drawer-list > a[selected] {
    color: var(--app-primary-color);
  }

  /* Workaround for IE11 displaying <main> as inline */
  main {
    display: block;
  }

  .main-content {
    box-sizing: border-box;
    padding: 24px 8px 8px 8px;
    overflow: auto;
  }

  .page {
    display: none;
  }

  .page[active] {
    display: block;
  }

  footer {
    box-sizing: border-box;
    padding: 24px;
    color: var(--app-drawer-text-color);
    text-align: center;
    background: var(--app-drawer-background-color);
  }

  /* Wide layout: when the viewport width is bigger than 460px, layout
  changes to a wide layout */
  @media (min-width: 460px) {
    .toolbar-list {
      display: block;
    }

    /* The drawer button isn't shown in the wide layout, so we don't
    need to offset the title */
    [main-title] {
      padding-right: 0;
    }
  }
`;

const vaadinTextFieldStyle = css`
  [part='input-field'] {
    background-color: transparent;
    border: 1px solid rgba(0, 0, 0, 0.38);
  }

  [part='label'],
  [part='value'] {
    font-weight: 400;
    color: var(--app-dark-text-color);
  }

  :host([focused]:not([readonly])) [part='label'] {
    color: var(--app-primary-color);
  }
`;

// This decorator defines the element.
@customElement('app-shell')
class AppShell extends CoreElement {
  // This decorator creates a property accessor that triggers rendering and
  // an observed attribute.
  @property({ type: String }) appTitle;
  @property({ type: Boolean }) _isSnackbarOpened = false;

  static get styles() {
    return [style];
  }

  render() {
    // Anything that's related to rendering should be done in here.
    return html`
      <mwc-drawer id="drawer" hasHeader type="dismissible">
        <span slot="title">Menu</span>
        <!-- <span slot="subtitle">subtitle</span> -->
        <div class="drawer-content">
          <nav class="drawer-list">
            <p><b>Atlas Čechů</b></p>
            <a
              ?selected="${this.store.page === 'users' && this.store.subPage === 'atlas-cechu'}"
              ?hidden="${!this.store.isUserSignedIn}"
              href="/users/atlas-cechu"
              >Uživatelé</a
            >
            <a
              ?selected="${this.store.page === 'data' && this.store.subPage === 'atlas-cechu'}"
              ?hidden="${!this.store.isUserSignedIn}"
              href="/data/atlas-cechu"
              >Data</a
            >
            <p><b>FEG Dashboard</b></p>
            <a
              ?selected="${this.store.page === 'users' && this.store.subPage === 'feg-dashboard'}"
              ?hidden="${!this.store.isUserSignedIn}"
              href="/users/feg-dashboard"
              >Uživatelé</a
            >
            <a
              ?selected="${this.store.page === 'data' && this.store.subPage === 'feg-dashboard'}"
              ?hidden="${!this.store.isUserSignedIn}"
              href="/data/feg-dashboard"
              >Data</a
            >
            <br />
            <a ?hidden="${this.store.isUserSignedIn}" href="/signin.html">Přihlásit se</a>
            <a ?hidden="${!this.store.isUserSignedIn}" href="/signout">Odhlásit se</a>
          </nav>
        </div>
        <div slot="appContent">
          <mwc-top-app-bar>
            <mwc-icon-button slot="navigationIcon" icon="menu"></mwc-icon-button>
            <div slot="title">${this.appTitle}</div>
          </mwc-top-app-bar>

          <!-- Main content -->
          <main role="main" class="main-content">
            <access-denied-view class="page" ?active="${this.store.page === 'access-denied'}"></access-denied-view>
            <data-view class="page" ?active="${this.store.page === 'data'}" .store="${this.store}"></data-view>
            <home-view class="page" ?active="${this.store.page === 'home'}"></home-view>
            <not-found-view class="page" ?active="${this.store.page === 'not-found'}"></not-found-view>
            <users-view class="page" ?active="${this.store.page === 'users'}" .store="${this.store}"></users-view>
          </main>
        </div>
      </mwc-drawer>

      <!-- <footer>
        <p>Made with &hearts; by the Behavio team.</p>
      </footer> -->

      <snack-bar ?active="${this._isSnackbarOpened}">
        You are now ${this.store.isAppOffline ? 'offline' : 'online'}.
      </snack-bar>
    `;
  }

  constructor() {
    super();

    registerStyles('vaadin-text-field', vaadinTextFieldStyle);
  }

  firstUpdated() {
    this._updateStore({ appTitle: this.appTitle });
    installOfflineWatcher(offline => this._offlineChanged(offline));
    installMediaQueryWatcher(`(min-width: 460px)`, matches => this._layoutChanged(matches));

    const drawer = this.shadowRoot.getElementById('drawer');
    drawer.addEventListener('MDCTopAppBar:nav', () => {
      drawer.open = !drawer.open;
    });
  }

  updated(changedProperties) {
    logChangedProperties(this, changedProperties);
    this._watchStore(changedProperties);
  }

  _layoutChanged(isWideLayout) {
    // The drawer doesn't make sense in a wide layout, so if it's opened, close it.
    if (isWideLayout) this.shadowRoot.getElementById('drawer').open = true;
  }

  _offlineChanged(offline) {
    const previousOffline = this.store.isAppOffline;
    this._updateStore({ isAppOffline: offline });

    // Don't show the snackbar on the first load of the page.
    if (previousOffline === undefined) {
      return;
    }

    clearTimeout(this.__snackbarTimer);
    this._isSnackbarOpened = true;
    this.__snackbarTimer = setTimeout(() => {
      this._isSnackbarOpened = false;
    }, 3000);
  }
}
